import { Bell, Check, Trash2, Activity, Wallet, Zap, Plug, Cpu } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications, timeAgo, type Notification } from "@/lib/notifications-context";
import { cn } from "@/lib/utils";

const ICON: Record<Notification["kind"], typeof Bell> = {
  job: Cpu, wallet: Wallet, bid: Zap, system: Activity, connect: Plug,
};

export function NotificationsPopover() {
  const { items, unread, markAllRead, markRead, clear } = useNotifications();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label="Notifications"
          className="relative rounded-xl border hairline bg-soft p-2 text-muted-foreground transition hover:text-foreground"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-semibold text-primary-foreground"
              style={{ background: "var(--gradient-primary)" }}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between border-b hairline px-3.5 py-2.5">
          <div>
            <p className="text-sm font-semibold">Notifications</p>
            <p className="text-[10px] text-muted-foreground">{unread} unread · {items.length} total</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={markAllRead}
              disabled={unread === 0}
              title="Mark all as read"
              className="rounded-md p-1.5 text-muted-foreground transition hover:bg-soft hover:text-foreground disabled:opacity-40"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={clear}
              disabled={items.length === 0}
              title="Clear all"
              className="rounded-md p-1.5 text-muted-foreground transition hover:bg-soft hover:text-foreground disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-3.5 py-10 text-center text-xs text-muted-foreground">You're all caught up.</div>
          )}
          {items.map((n) => {
            const Icon = ICON[n.kind];
            return (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                className={cn(
                  "flex w-full items-start gap-3 border-b hairline px-3.5 py-3 text-left transition hover:bg-soft last:border-b-0",
                  !n.read && "bg-soft/40",
                )}
              >
                <div
                  className={cn(
                    "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border hairline",
                    !n.read && "text-foreground",
                  )}
                  style={!n.read ? { background: "var(--gradient-primary)", borderColor: "transparent" } : undefined}
                >
                  <Icon className={cn("h-3.5 w-3.5", !n.read && "text-primary-foreground")} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="truncate text-xs font-semibold">{n.title}</p>
                    <span className="shrink-0 text-[10px] text-muted-foreground">{timeAgo(n.createdAt)}</span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{n.body}</p>
                </div>
                {!n.read && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--gradient-primary)" }} />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}